import Route from '@ember/routing/route'
import type RouterService from '@ember/routing/router-service'
import type Transition from '@ember/routing/transition'
import { task, timeout } from 'ember-concurrency'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import type { Precinct } from 'teacher-ui-client/utils/precincts'
import { isPrecinct, isValidPrecinctForProduct, defaultPrecinctForProduct } from 'teacher-ui-client/utils/precincts'
import { deserializeFilterPhase } from 'teacher-ui-client/components/lessons/fast-phonics/phase-filters'
import type ContentfulClient from 'teacher-ui-client/services/contentful-client'
import { LessonResourceType, isLessonResourceType } from 'teacher-ui-client/contentful-client/entry-types'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { assert } from '@ember/debug'

interface Params extends Record<string, unknown> {
  precinct: string
}

export default class FpLessonsResourcesPage extends Route {
  @service contentfulClient!: ContentfulClient

  @service router!: RouterService

  @queryParam('subscription-type/lessons') precinct!: string

  queryParams = {
    resourceType: {
      replace: true,
      refreshModel: true,
    },
  }

  beforeModel(transition: Transition) {
    const resourceType = transition.to?.queryParams.resourceType
    if (!isLessonResourceType(resourceType)) {
      void this.router.transitionTo({ queryParams: { resourceType: LessonResourceType.WEEKLY_TEACHING_GUIDE } })
      return
    }
  }

  model(params: Params, transition: Transition) {
    const precinct =
      isPrecinct(params.precinct) && isValidPrecinctForProduct(Product.FP, params.precinct)
        ? params.precinct
        : defaultPrecinctForProduct(Product.FP)

    // LessonResourceType is asserted here, as beforeModel transitions away if the type isn't LessonResourceType
    const resourceType = transition.to?.queryParams.resourceType
    assert('must be LessonResourceType', isLessonResourceType(resourceType))

    const precincts = [precinct]
    const filterPhase = transition.to?.queryParams.filterPhase
    const selectedPhases = deserializeFilterPhase(typeof filterPhase === 'string' ? filterPhase : '')

    return { loaderInstance: this.fetchEntries.perform(precincts, resourceType, selectedPhases) }
  }

  fetchEntries = task(
    { restartable: true },
    async (precincts: Precinct[], resourceType: LessonResourceType, selectedPhases?: number[]) => {
      await timeout(500)
      return this.contentfulClient.getLessonResources(precincts, [resourceType], { phases: selectedPhases })
    },
  )
}
